import PropTypes from 'prop-types';
import { MIN_DOOR, doorWidth } from '../../../game/zoneShapes';

/**
 * The selected building: what it is called, what it is, and how to get in.
 *
 * THE DOOR WIDTH IS SHOWN AS "n of m", not just as a number to type. A door on
 * a north or south wall needs two tiles and one on an east or west wall needs
 * one — because feet are 18 units wide and 10 tall against 16-unit tiles. That
 * asymmetry surprises everyone the first time, so the panel states the minimum
 * next to the actual and turns red rather than leaving you to discover it by
 * walking into your own shop.
 */

const KINDS = [
  { id: 'building', name: 'Building' },
  { id: 'shop', name: 'Shop' },
  { id: 'house', name: 'House' },
  { id: 'landmark', name: 'Landmark' },
  { id: 'area', name: 'Area' },
];

const SIDES = [
  { id: 'n', name: 'North' },
  { id: 's', name: 'South' },
  { id: 'e', name: 'East' },
  { id: 'w', name: 'West' },
];

const field = 'mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900';

function ZoneInspector({ zone, onChange, onRemove }) {
  if (!zone) {
    return (
      <p className="rounded border border-dashed border-gray-300 p-3 text-xs text-gray-500 dark:border-gray-700">
        Drag on the map to draw a building, or click one to edit it.
        <br />
        <br />
        A building is a labelled rectangle. Its walls are generated for you —
        there is no art for a shop yet, so this is how you say where one goes.
      </p>
    );
  }

  const doors = zone.doors || [];

  const setDoor = (index, patch) => onChange({
    doors: doors.map((d, i) => (i === index ? { ...d, ...patch } : d)),
  });

  return (
    <div className="space-y-3 rounded border border-gray-200 p-3 dark:border-gray-800">
      <label className="block text-sm" htmlFor="zone-label">
        <span className="text-gray-700 dark:text-gray-300">Name</span>
        <input
          id="zone-label"
          value={zone.label || ''}
          maxLength={24}
          placeholder="General Store"
          onChange={(e) => onChange({ label: e.target.value })}
          className={field}
        />
      </label>

      <label className="block text-sm" htmlFor="zone-kind">
        <span className="text-gray-700 dark:text-gray-300">Kind</span>
        <select
          id="zone-kind"
          value={zone.kind}
          onChange={(e) => onChange({ kind: e.target.value })}
          className={field}
        >
          {KINDS.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </label>

      <p className="text-xs text-gray-500">
        {zone.w}
        ×
        {zone.h}
        {' tiles at ('}
        {zone.x}
        {', '}
        {zone.y}
        )
      </p>

      <div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700 dark:text-gray-300">Doors</span>
          <button
            type="button"
            onClick={() => onChange({ doors: [...doors, { side: 'n', at: 1, span: 2 }] })}
            className="rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-800 dark:text-gray-300"
          >
            Add
          </button>
        </div>

        {doors.length === 0 && (
          <p className="mt-1 text-xs text-red-500">
            No door — nobody can get inside.
          </p>
        )}

        {doors.map((door, i) => {
          const needed = MIN_DOOR[door.side] || 1;
          const actual = doorWidth(zone, door);
          const tooNarrow = actual < needed;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="mt-2 space-y-1 rounded bg-gray-50 p-2 dark:bg-gray-900">
              <div className="flex gap-1">
                <select
                  value={door.side}
                  onChange={(e) => setDoor(i, { side: e.target.value })}
                  className="flex-1 rounded border border-gray-300 px-1 py-0.5 text-xs dark:border-gray-700 dark:bg-gray-900"
                  aria-label="Which wall"
                >
                  {SIDES.map((sd) => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => onChange({ doors: doors.filter((_, j) => j !== i) })}
                  className="rounded bg-gray-200 px-2 text-xs dark:bg-gray-800 dark:text-gray-300"
                >
                  ✕
                </button>
              </div>

              <div className="flex gap-1 text-xs">
                <label className="flex-1" htmlFor={`door-at-${i}`}>
                  <span className="text-gray-500">Along</span>
                  <input
                    id={`door-at-${i}`}
                    type="number"
                    min="1"
                    value={door.at}
                    onChange={(e) => setDoor(i, { at: Math.max(1, Number(e.target.value)) })}
                    className="w-full rounded border border-gray-300 px-1 py-0.5 dark:border-gray-700 dark:bg-gray-900"
                  />
                </label>
                <label className="flex-1" htmlFor={`door-span-${i}`}>
                  <span className="text-gray-500">Wide</span>
                  <input
                    id={`door-span-${i}`}
                    type="number"
                    min="1"
                    value={door.span}
                    onChange={(e) => setDoor(i, { span: Math.max(1, Number(e.target.value)) })}
                    className="w-full rounded border border-gray-300 px-1 py-0.5 dark:border-gray-700 dark:bg-gray-900"
                  />
                </label>
              </div>

              <p className={`text-[11px] ${tooNarrow ? 'text-red-500' : 'text-gray-500'}`}>
                {`Opens ${actual} of the ${needed} tile(s) a player needs.`}
                {tooNarrow && (
                  <>
                    <br />
                    {door.side === 'n' || door.side === 's'
                      ? 'Feet are 18 units across against 16-unit tiles, so a gap in a horizontal wall needs two.'
                      : 'This door opens nothing — it sits on a corner or past the end of the wall.'}
                  </>
                )}
              </p>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onRemove(zone.id)}
        className="w-full rounded bg-red-500/10 px-2 py-1 text-xs text-red-500 hover:bg-red-500/20"
      >
        Delete this building
      </button>
    </div>
  );
}

ZoneInspector.propTypes = {
  zone: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    kind: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    w: PropTypes.number,
    h: PropTypes.number,
    doors: PropTypes.arrayOf(PropTypes.shape({
      side: PropTypes.string,
      at: PropTypes.number,
      span: PropTypes.number,
    })),
  }),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

ZoneInspector.defaultProps = { zone: null };

export default ZoneInspector;
